import React from 'react';
import { useTranslation } from 'react-i18next';
import { GownColor } from '../../api/StoreOffersAPI';
import { Check } from 'lucide-react';

type Props = {
  label?: string;
  value: GownColor;
  onChange: (color: GownColor) => void;
  isDisabled?: boolean;
};

const COLOR_MAP: Record<GownColor, { hex: string; name: string }> = {
  white: { hex: '#FFFFFF', name: 'store.form.gown_color.white' },
  ivory: { hex: '#FFFFF0', name: 'store.form.gown_color.ivory' },
  blush: { hex: '#FFF0F5', name: 'store.form.gown_color.blush' },
  champagne: { hex: '#F7E7CE', name: 'store.form.gown_color.champagne' },
  silver: { hex: '#C0C0C0', name: 'store.form.gown_color.silver' },
  gold: { hex: '#FFD700', name: 'store.form.gown_color.gold' },
  other: { hex: '#E5E5E5', name: 'store.form.gown_color.other' },
};

function ColorPicker({ label, value, onChange, isDisabled = false }: Props) {
  const { t } = useTranslation();
  const colors: GownColor[] = ['white', 'ivory', 'blush', 'champagne', 'silver', 'gold', 'other'];
  
  // Safety check - ensure value is valid
  const safeValue: GownColor = value || 'white';
  const colorInfo = COLOR_MAP[safeValue] || COLOR_MAP.white;

  return (
    <div className="flex-1 max-w-full">
      {label && <p className="text-sm font-extrabold mb-2">{t(label)}:</p>}
      <div className="flex flex-wrap gap-2">
        {colors.map((color) => {
          const colorInfo = COLOR_MAP[color];
          const isSelected = safeValue === color;
          const isWhite = color === 'white';
          
          return (
            <button
              key={color}
              type="button"
              onClick={() => !isDisabled && onChange(color)}
              disabled={isDisabled}
              className={`
                relative w-12 h-12 rounded-lg border-2 transition-all duration-200
                ${isSelected 
                  ? 'border-primary-600 scale-110 shadow-md' 
                  : 'border-gray-300 hover:border-gray-400 hover:scale-105'
                }
                ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                ${isWhite ? 'bg-white' : ''}
              `}
              style={{ backgroundColor: colorInfo.hex }}
              title={t(colorInfo.name) || color}
            >
              {isSelected && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Check className="h-5 w-5 text-primary-600 stroke-[3]" />
                </div>
              )}
              {isWhite && (
                <div className="absolute inset-0 border border-gray-200 rounded-lg" />
              )}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-gray-500 mt-1">
        {t(colorInfo.name) || safeValue}
      </p>
    </div>
  );
}

export default ColorPicker;

